import { Component, Injectable, OnDestroy, OnInit } from '@angular/core';
import { CollectionViewer, SelectionChange, DataSource } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Router } from '@angular/router';

import { BehaviorSubject, merge, Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { AppState, getMyUtenti, getMyAuth } from 'src/app/store/app.state';
import { UtentiService } from 'src/app/utenti/utenti.service';
import { StartAddUserResource, StartRemoveUserResource } from 'src/app/utenti/store';
import { Utente } from '../../models/utente.model';
import { attributesResource, Resource } from 'src/app/shared/models/resource.model';
import { SelectResource } from 'src/app/traduzioni/store';

/**Oggetto che rappresenta un nodo dinamico del mat-tree */
export class DynamicResourceNode {
  /**
   * Costruttore per la classe DynamicResourceNode
   * @param id {number} identificativo della risorsa
   * @param index {string} indice della risorsa
   * @param type {string} tipo di risorsa
   * @param title {string} titolo della risorsa
   * @param description {string} descrizione della risorsa
   * @param attributes {attributesResource} insieme di attributi della risorsa
   * @param username {string} nome dell'ultimo account che ha modificato la risorsa
   * @param accountType {string} ruolo dell'ultimo account che ha modificato la risorsa
   * @param locked {boolean} se la risorsa è bloccata o meno
   * @param level {number} livello del nodo nell'albero
   * @param expandable {boolean} se il nodo è espandibile, quindi se ha figli
   */
  constructor(
    public id: number,
    public index: string,
    public type: string,
    public title: string,
    public description: string,
    public attributes: attributesResource,
    public username: string,
    public accountType: string,
    public locked: boolean,
    public level = 1,
    public expandable = false,
    public expanded = false
  ) { }
}

/**Classe che gestisce il recupero dei dati del mio elenco annidato di risorse */
@Injectable({ providedIn: 'root' })
export class DynamicResourceDatabase implements OnDestroy {
  /**Array delle risorse di primo livello */
  rootLevelNodes: Resource[];

  childrenSubs: Subscription;

  /**
   * Costruttore della classe DynamicResourceDatabase
   * @param store {Store<AppState>} stato a livello dell'applicazione
   * @param utentiService {UtentiService} servizi della sezione utenti
   */
  constructor(
    private store: Store<AppState>,
    private utentiService: UtentiService
  ) { }

  /**
   * Metodo che recupera dallo store l'elenco dei trattati come Resource[] e trasforma in un DynamicResourceNode[]
   * @returns array delle risorse di primo livello nel formato previsto per i nodi del mat-tree
   */
  initialData(): DynamicResourceNode[] {
    this.store.select(getMyUtenti).pipe(take(1)).subscribe(data => {
      this.rootLevelNodes = data.elencoTrattati;
    });
    return this.rootLevelNodes.map(data => new DynamicResourceNode(
      data.id,
      data.index,
      data.type,
      data.title,
      data.description,
      data.attributes,
      data.username,
      data.accountType,
      data.locked,
      0,
      true,
      false
    ));
  }

  /**
   * Metodo che recupera l'array di sottorisorse di una risorsa data
   * @param node {Resource} risorsa della quale si devono recuperare le sottorisorse
   * @returns una promise dell'array di sottorisorse
   */
  getChildren(node: Resource): Promise<Resource[]> {
    let test = this.utentiService.getSubResourcesList(node.id);
    return new Promise((resolve, reject) => {
      this.childrenSubs = test.subscribe((response: Resource[]) => {
        resolve(response);
      }, reject);
    });
  }

  /**
   * Metodo che valuta se una risorsa è espandibile, quindi se ha figli, sulla base del tipo
   * @param node {Resource} risorsa da valutare
   * @returns true per risorsa con figli, altrimenti false
   */
  isExpandable(node: Resource): boolean {
    if (node.type !== 'Logical Unit') {
      return true;
    } else {
      return false;
    }
  }

  ngOnDestroy() {
    if (this.childrenSubs) {
      this.childrenSubs.unsubscribe();
    }
  }
}

/**Classe che implementa l'interfaccia DataSource di cdk/tree per il nostro caso specifico */
@Injectable({ providedIn: 'root' })
export class DynamicResourceDataSource implements DataSource<DynamicResourceNode>, OnDestroy {
  /** */
  dataChange = new BehaviorSubject<DynamicResourceNode[]>([]);

  subsTreeControlChange: Subscription;

  /**Getter dei dati che recupera il valore inserito in dataChange */
  get data(): DynamicResourceNode[] { return this.dataChange.value; }

  /**Setter dei dati dell'albero, ne emette il valore */
  set data(value: DynamicResourceNode[]) {
    this._treeControl.dataNodes = value;
    this.dataChange.next(value);
  }

  /**
   * Costruttore per la classe DynamicResourceDataSource
   * @param _treeControl {FlatTreeControl<DynamicResourceNode>}
   * @param _database {DynamicResourceDatabase}
   */
  constructor(
    private _treeControl: FlatTreeControl<DynamicResourceNode>,
    private _database: DynamicResourceDatabase
  ) { }

  /**
   * Metodo dell'interfaccia DataSource che connette il modello di espansione dell'albero a un gestore custom, sottoscrivendo l'evento changed sia esso in espansione (added) o compressione (removed)
   * @param collectionViewer {CollectionViewer}
   */
  connect(collectionViewer: CollectionViewer): Observable<DynamicResourceNode[]> {
    this.subsTreeControlChange = this._treeControl.expansionModel.changed.subscribe(change => {
      if ((change as SelectionChange<DynamicResourceNode>).added || (change as SelectionChange<DynamicResourceNode>).removed) {
        this.handleTreeControl(change as SelectionChange<DynamicResourceNode>);
      }
    });

    return merge(collectionViewer.viewChange, this.dataChange).pipe(map(() => this.data));
  }

  /**
   * Metodo richiesto dall'interfaccia DataSource
   * @param collectionViewer {CollectionViewer}
   */
  disconnect(collectionViewer: CollectionViewer): void { }

  /**Metodo che gestisce gli eventi di cambiamento, espansione e compressione, di un nodo dell'albero, richiamando il toggle custom con i parametri appropriati
   * @param change {SelectionChange<DynamicResourceNode>}
   */
  handleTreeControl(change: SelectionChange<DynamicResourceNode>) {
    if (change.added) {
      change.added.forEach(node => this.toggleNode(node, true));
    }
    if (change.removed) {
      change.removed.slice().reverse().forEach(node => this.toggleNode(node, false));
    }
  }

  /**
   * Metodo che espande o comprime un nodo procedendo anche al recupero dei figli e al loro inserimento nell'array dei nodi o alla loro rimozione
   * @param node {DynamicResourceNode} il nodo dell'albero sul quale avviene la modifica
   * @param expand {boolean} se la modifica è di espansione
   */
  async toggleNode(node: DynamicResourceNode, expand: boolean) {
    let children: Resource[] = await this._database.getChildren(node);
    const index = this.data.indexOf(node);
    if (!children || index < 0) {
      return;
    }
    if (expand) {
      this.data[index].expanded = true;
      const nodes = children.map(elNode =>
        new DynamicResourceNode(
          elNode.id,
          elNode.index,
          elNode.type,
          elNode.title,
          elNode.description,
          elNode.attributes,
          elNode.username,
          elNode.accountType,
          elNode.locked,
          node.level + 1,
          this._database.isExpandable(elNode),
          false
        ));
      this.data.splice(index + 1, 0, ...nodes);
    } else {
      this.data[index].expanded = false;
      let count = 0;
      for (let i = index + 1; i < this.data.length && this.data[i].level > node.level; i++, count++) { }
      this.data.splice(index + 1, count);
    }
    this.dataChange.next(this.data);
  }

  ngOnDestroy() {
    if (this.subsTreeControlChange) {
      this.subsTreeControlChange.unsubscribe();
    }
  }
}

/**Componente che rappresenta l'albero delle risorse disponibili per l'applicazione */
@Component({
  selector: 'app-resource-tree',
  templateUrl: './resource-tree.component.html',
  styleUrls: ['./resource-tree.component.css']
})
export class ResourceTreeComponent implements OnInit, OnDestroy {
  treeControl: FlatTreeControl<DynamicResourceNode>;
  dataSource: DynamicResourceDataSource;
  getLevel = (node: DynamicResourceNode) => node.level;
  isExpandable = (node: DynamicResourceNode) => node.expandable;
  hasChild = (_: number, _nodeData: DynamicResourceNode) => _nodeData.expandable;
  /**Pagina corrente dell'applicazione presa dall'url */
  currentPage: string;
  /**Elenco delle risorse assegnate all'utente */
  assignedList: Resource[];
  /**Sottoscrizione per il recupero dell'elenco risorse assegnate */
  assignedSubs: Subscription;

  selectedResource: Resource;

  loggedUser: Utente;

  /**
   * Costruttore per la classe ResourceTreeComponent
   * @param database {DynamicResourceDatabase}
   * @param router {Router}
   * @param store {Store<AppState>}
   */
  constructor(
    database: DynamicResourceDatabase,
    private router: Router,
    private store: Store<AppState>
  ) {
    this.currentPage = this.router.url.split('/')[1];
    this.treeControl = new FlatTreeControl<DynamicResourceNode>(this.getLevel, this.isExpandable);
    this.dataSource = new DynamicResourceDataSource(this.treeControl, database);
    this.dataSource.data = database.initialData();
    if (this.currentPage === 'utenti') {
      this.assignedSubs = this.store.select(getMyUtenti).pipe(take(1)).subscribe(data => {
        this.assignedList = data.elencoRisorse.slice();
      })
    }
    this.store.select(getMyAuth).pipe(take(1)).subscribe(data => {
      this.loggedUser = data.loggedUser;
    })
  }

  ngOnInit() {
    if (this.currentPage === 'traduzioni') {
      if (this.loggedUser && this.loggedUser.settings.startIndex) {
        let indexArray = this.loggedUser.settings.startIndex.split('.');
        this.preSelectionTraduzioni(0, indexArray);
      }
    }
  }

  /**
   * Metodo che sovrascrive matTreeNodeToggle permettendo di passare come parametro di espansione la proprietà expanded del singolo nodo
   * @param node {DynamicResourceNode} nodo sul quale si è chiamata l'espansione o compressione
   */
  onToggleFromIcon(node: DynamicResourceNode) {
    this.dataSource.toggleNode(node, !node.expanded);
  }

  async preSelectionTraduzioni(indiceSelezione: number, indexArray: string[]) {
    indiceSelezione = indiceSelezione + Number(indexArray[0]);
    if (indexArray.length === 1) {
      this.onRowSelection(this.dataSource.data[indiceSelezione - 1]);
    } else {
      await this.dataSource.toggleNode(this.dataSource.data[indiceSelezione - 1], true);
      this.preSelectionTraduzioni(indiceSelezione, indexArray.splice(1, indexArray.length - 1));
    }
  }

  /**
   * Metodo che gestisce gli eventi successivi alla selezione di una risorsa
   * @param node {any} nodo dell'albero delle risorse selezionato
   */
  //TODO servirà per la sezione traduzioni e il recupero delle risorse relative
  onRowSelection(node) {
    this.selectedResource = <Resource>{
      id: node.id,
      index: node.index,
      type: node.type,
      title: node.title,
      description: node.description,
      attributes: node.attributes,
      username: node.username,
      accountType: node.accountType,
      locked: node.locked
    };
    this.store.dispatch(new SelectResource(this.selectedResource));
  }

  /**
   * Metodo che gestisce l'aggiunta o rimozione di una risorsa assegnata quando cambia il flag su una checkbox
   * @param node {any} nodo dell'albero delle risorse
   * @param checked {any} valore della checkbox
   */
  onCheckChange(node, checked) {
    let utenteSelezionato: Utente;
    this.store.select(getMyUtenti).pipe(take(1)).subscribe(data => {
      utenteSelezionato = data.utenteSelezionato;
    });
    let risorsaTemp = <Resource>{
      id: node.id,
      index: node.index,
      type: node.type,
      title: node.title,
      description: node.description,
      attributes: node.attributes,
      username: node.username,
      accountType: node.accountType,
      locked: node.locked
    };
    if (checked) {
      this.store.dispatch(new StartAddUserResource({
        userId: utenteSelezionato.id,
        resource: risorsaTemp
      }))
    } else {
      this.store.dispatch(new StartRemoveUserResource({ userId: utenteSelezionato.id, resourceId: risorsaTemp.id }));
    }
  }

  trackByFn(index, item): number {
    return item.id;
  }

  /**Metodo dell'interfaccia OnDestroy che usiamo per eliminare eventuali sottoscrizioni presenti */
  ngOnDestroy() {
    if (this.assignedSubs) {
      this.assignedSubs.unsubscribe();
    }
  }
}